import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

// Attach current user email to every request as a fallback for auth when cookies are unavailable
api.interceptors.request.use((config) => {
  try {
    const saved = localStorage.getItem("usair_user");
    if (saved) {
      const u = JSON.parse(saved);
      if (u?.email) config.headers["X-User-Email"] = u.email;
    }
  } catch { /* ignore */ }
  // iter124 — Conversation system bearer session: when an external user has
  // redeemed a magic-link OTP, their session token is stored under
  // `usair_conv_session`. Send it as Authorization: Bearer cs_<…> on every
  // /conv/* request so the backend's _resolve_actor() can recognise them.
  //
  // When a full portal user (Google OAuth / localStorage usair_user) is
  // also present, we skip the conv-session header — the portal session
  // is authoritative and stale conv sessions would otherwise shadow it
  // (backend now falls through anyway, but not sending the header at all
  // is cleaner and marginally faster).
  try {
    const sess = localStorage.getItem("usair_conv_session");
    const saved = localStorage.getItem("usair_user");
    const url = String(config.url || "");
    if (sess && !saved && url.startsWith("/conv")) {
      config.headers["Authorization"] = `Bearer ${sess}`;
    }
  } catch { /* ignore */ }
  // Forward the active UI language so backend AI prompts (warranty summary,
  // assistant chat, etc.) reply in the user's language. Read from the same
  // localStorage key used by lib/i18n.jsx — no hard import to avoid cycles.
  try {
    const lang = localStorage.getItem("usair_lang");
    if (lang) config.headers["X-User-Lang"] = lang;
  } catch { /* ignore */ }
  return config;
});

/**
 * Global error interceptor — surfaces silent backend crashes to the user
 * via a sonner toast with a "Copy details" action.
 *
 * Strategy:
 *   • 5xx ("Internal server error") — ALWAYS toast. These are backend bugs;
 *     the user should know rather than seeing "nothing happened".
 *   • 4xx — skip. Calling code typically renders inline error states.
 *   • Network / timeout — toast (treat as 0).
 *
 * Each 5xx response from the backend now carries an `incident_id` (set by
 * server.py's _generic_exc_handler_logged via error_log_routes.write_error_row).
 * We surface that ID first because it's the fastest path to root cause:
 * admin can paste it into /admin/error-log/{id} and read the full stack trace.
 */
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    try {
      const status = error?.response?.status ?? 0;
      const isNetwork = !error?.response;
      const cfg = error?.config || {};
      // iter136.16 — opt-out for background polls. Endpoints that fire
      // silently on a timer (badge counts, presence pings) shouldn't
      // surface a noisy toast on every transient network blip — they
      // retry on their own cadence. Calls pass ``{ silent: true }`` in
      // the axios config to opt in.
      const silent = !!cfg.silent;
      const shouldToast = !silent && (isNetwork || status >= 500);
      if (!shouldToast) {
        return Promise.reject(error);
      }
      const incidentId = error?.response?.data?.incident_id || "";
      const detail = error?.response?.data?.detail || error?.message || "Network error";
      const path = (cfg.url || "").replace(/^\/+/, "/");
      const method = (cfg.method || "").toUpperCase();
      const headline = isNetwork
        ? "Network error — couldn't reach the server"
        : (incidentId
            ? `Error ${incidentId} — server hit a snag`
            : "Server error");
      const subline = `${method} ${path}: ${String(detail).slice(0, 160)}`;
      const detailBlob = JSON.stringify({
        when: new Date().toISOString(),
        method, path,
        status,
        incident_id: incidentId,
        detail,
        url: cfg.baseURL ? `${cfg.baseURL}${path}` : path,
      }, null, 2);
      toast.error(headline, {
        description: subline,
        duration: 10000,
        action: {
          label: "Copy details",
          onClick: () => {
            try {
              navigator.clipboard?.writeText(detailBlob);
              toast.success("Error details copied — paste in chat or Slack.");
            } catch { /* ignore */ }
          },
        },
      });
    } catch { /* never blow up the original promise */ }
    return Promise.reject(error);
  },
);

export const checkDbStatus = () => api.get("/db-status").then(r => r.data);

// Parts
export const fetchParts = (params) => api.get("/parts", { params }).then(r => r.data);
// iter136.29 — distinct compressor Product_Types that have at least one PM-kit row registered.
export const fetchPmProductTypes = () => api.get("/compressors/pm-product-types").then(r => r.data);
// iter136.30 — sellable stock (US/CA) per Parts._ComputedKey. Backend returns
// 403 for customers and unauthenticated guests, so callers must guard.
export const fetchSellableStock = () => api.get("/compressors/sellable-stock").then(r => r.data);
export const fetchPart = (id) => api.get(`/parts/${id}`).then(r => r.data);
export const createPart = (data) => api.post("/parts", data).then(r => r.data);
export const updatePart = (id, data) => api.put(`/parts/${id}`, data).then(r => r.data);

// Customers
export const fetchCustomers = (params) => api.get("/customers", { params }).then(r => r.data);
export const fetchCustomer = (id) => api.get(`/customers/${id}`).then(r => r.data);
export const fetchCustomerPersons = (customerID) => api.get(`/customers/${encodeURIComponent(customerID)}/persons`).then(r => r.data);
export const fetchCustomerQuotes = (customerID, limit = 50) => api.get(`/customers/${encodeURIComponent(customerID)}/quotes`, { params: { limit } }).then(r => r.data);
export const fetchCustomerCompressors = (customerID, limit = 200) => api.get(`/customers/${encodeURIComponent(customerID)}/compressors`, { params: { limit } }).then(r => r.data);
export const fetchCustomerPartsPurchased = (customerID, params = {}) => api.get(`/customers/${encodeURIComponent(customerID)}/parts-purchased`, { params }).then(r => r.data);
export const fetchCustomerJournal = (customerID, limit = 200) => api.get(`/customers/${encodeURIComponent(customerID)}/journal`, { params: { limit } }).then(r => r.data);
export const fetchCustomerPhoneLogs = (customerID, limit = 100) => api.get(`/customers/${encodeURIComponent(customerID)}/phone-logs`, { params: { limit } }).then(r => r.data);
export const fetchCustomerNotes = (customerID, limit = 200) => api.get(`/customers/${encodeURIComponent(customerID)}/notes`, { params: { limit } }).then(r => r.data);
export const fetchCompressorPMSchedule = (serialId) => api.get(`/compressors/${encodeURIComponent(serialId)}/pm-schedule`).then(r => r.data);
export const fetchCompressorServiceForecast = (serialId) => api.get(`/compressors/${encodeURIComponent(serialId)}/service-forecast`).then(r => r.data);
export const fetchCompressorPhoneLogs = (serialId, limit = 100) => api.get(`/compressors/${encodeURIComponent(serialId)}/phone-logs`, { params: { limit } }).then(r => r.data);
export const fetchPersonPhoneLogs = (personId, limit = 100) => api.get(`/persons/${encodeURIComponent(personId)}/phone-logs`, { params: { limit } }).then(r => r.data);
export const fetchQuote = (headingID) => api.get(`/quotes/${encodeURIComponent(headingID)}`).then(r => r.data);
// iter150AI — dealer workflow.
export const fetchQuoteDealerState = (headingID) =>
  api.get(`/quotes/${encodeURIComponent(headingID)}/dealer-state`).then(r => r.data);
export const submitQuoteForApproval = (headingID, payload = {}) =>
  api.post(`/quotes/${encodeURIComponent(headingID)}/submit-for-approval`, payload).then(r => r.data);
export const approveQuote = (headingID, payload) =>
  api.post(`/quotes/${encodeURIComponent(headingID)}/approve`, payload).then(r => r.data);
export const rejectQuote = (headingID, payload) =>
  api.post(`/quotes/${encodeURIComponent(headingID)}/reject`, payload).then(r => r.data);
export const fetchApprovalEmails = () =>
  api.get(`/admin/quote-approval-emails`).then(r => r.data);
export const saveApprovalEmails = (emails) =>
  api.put(`/admin/quote-approval-emails`, { emails }).then(r => r.data);
export const fetchQuoteApprovalHistory = (headingID) =>
  api.get(`/quotes/${encodeURIComponent(headingID)}/approval-history`).then(r => r.data);
export const fetchHelpSection = (section, lang) =>
  api.get(`/help/sections/${encodeURIComponent(section)}`, { params: { lang } }).then(r => r.data);
export const updateQuoteHeading = (headingID, data) => api.put(`/quotes/${encodeURIComponent(headingID)}`, data).then(r => r.data);
export const addQuoteLine = (headingID, data) => api.post(`/quotes/${encodeURIComponent(headingID)}/details`, data).then(r => r.data);
export const updateQuoteLine = (headingID, detailID, data) => api.put(`/quotes/${encodeURIComponent(headingID)}/details/${encodeURIComponent(detailID)}`, data).then(r => r.data);
export const deleteQuoteLine = (headingID, detailID) => api.delete(`/quotes/${encodeURIComponent(headingID)}/details/${encodeURIComponent(detailID)}`).then(r => r.data);
export const searchParts = (q, limit = 20) => api.get(`/parts-search`, { params: { q, limit } }).then(r => r.data);
export const fetchLogbook = (params = {}) => api.get(`/logbook`, { params }).then(r => r.data);
export const fetchLogbookSettings = () => api.get(`/logbook/settings`).then(r => r.data);
export const updateLogbookSettings = (enabled, requester_email) => api.put(`/logbook/settings`, { enabled, requester_email }).then(r => r.data);
export const fetchLogbookArchiveStatus = () => api.get(`/logbook/archive/status`).then(r => r.data);
export const runLogbookArchive = (requester_email) => api.post(`/logbook/archive/run`, { requester_email }).then(r => r.data);
export const customerCounts = () => api.get("/customers/counts").then(r => r.data);
export const createCustomer = (data) => api.post("/customers", data).then(r => r.data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data).then(r => r.data);

// Admin management
export const fetchAdmins = () => api.get("/admins").then(r => r.data);
export const addAdmin = (email, requester_email) => api.post("/admins", { email, requester_email }).then(r => r.data);
export const removeAdmin = (email, requester_email) => api.delete(`/admins/${encodeURIComponent(email)}`, { params: { requester_email } }).then(r => r.data);

// DB credentials (admin)
export const fetchDbConfig = (requester_email) => api.get("/admin/db-config", { params: { requester_email } }).then(r => r.data);
export const testDbConfig = (payload) => api.post("/admin/db-config/test", payload).then(r => r.data);
export const saveDbConfig = (payload) => api.put("/admin/db-config", payload).then(r => r.data);
export const resetDbConfig = (requester_email) => api.delete("/admin/db-config", { params: { requester_email } }).then(r => r.data);

// Product classes (admin)
export const fetchProductClasses = (requester_email) => api.get("/product-classes", { params: { requester_email } }).then(r => r.data);
export const updateProductClass = (id, payload) => api.put(`/product-classes/${id}`, payload).then(r => r.data);

// Compressors by serial
export const fetchCompressors = (params) => api.get("/compressors", { params }).then(r => r.data);
export const compressorsExportUrl = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== "")
  ).toString();
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/compressors/export`;
  return qs ? `${base}?${qs}` : base;
};
export const fetchCompressorFiles = (serialId) => api.get(`/compressors/${serialId}/files`).then(r => r.data);
export const resolveCompressorFileUrl = (serialId, path) =>
  api.get(`/compressors/${serialId}/file-url`, { params: { path } }).then(r => r.data);
export const updateCompressorFile = (serialId, pbsid, payload) =>
  api.patch(`/compressors/${serialId}/files/${pbsid}`, payload).then(r => r.data);

// ─── AI Assistant ───
export const fetchAiGreeting = (email, lang = "en") =>
  api.get(`/ai/greeting`, { params: { email, lang } }).then(r => r.data);
export const listAiSessions = (email) => api.get(`/ai/sessions`, { params: { email } }).then(r => r.data);
export const createAiSession = (requester_email, title) => api.post(`/ai/sessions`, { requester_email, title }).then(r => r.data);
export const listAiMessages = (sid, email) => api.get(`/ai/sessions/${sid}/messages`, { params: { email } }).then(r => r.data);
export const sendAiChat = (session_id, message, requester_email, override_params = null) =>
  api.post(`/ai/chat`, {
    session_id,
    message,
    requester_email,
    ...(override_params ? { override_params } : {}),
  }).then(r => r.data);
export const listAiActionHistory = (email, limit = 50) =>
  api.get(`/ai/action-history`, { params: { email, limit } }).then(r => r.data);
export const undoAiAction = (action_id, requester_email) =>
  api.post(`/ai/action-history/${action_id}/undo`, { requester_email }).then(r => r.data);
export const fetchAiProvenance = (entity_type, entity_id, email) =>
  api.get(`/ai/provenance`, { params: { entity_type, entity_id, email } }).then(r => r.data);
export const listAiTraining = (email) => api.get(`/ai/training`, { params: { email } }).then(r => r.data);
export const addAiTraining = (requester_email, fact, source_session_id) => api.post(`/ai/training`, { requester_email, fact, source_session_id }).then(r => r.data);
export const deleteAiTraining = (tid, requester_email) => api.delete(`/ai/training/${tid}`, { data: { requester_email } }).then(r => r.data);
export const captureAiLead = (session_id, to_email, requester_email, note) =>
  api.post(`/ai/lead`, { session_id, to_email, requester_email, note }).then(r => r.data);
export const listAiLeads = (email, limit = 200) =>
  api.get(`/ai/leads`, { params: { email, limit } }).then(r => r.data);
export const markAiLeadContacted = (lead_id, requester_email) =>
  api.patch(`/ai/leads/${lead_id}/contacted`, { requester_email }).then(r => r.data);
export const previewAiDigest = (email) =>
  api.get(`/ai/digest/preview`, { params: { email } }).then(r => r.data);
export const sendAiDigestNow = (requester_email) =>
  api.post(`/ai/digest/send-now`, { requester_email }).then(r => r.data);
export const fetchAiLeadStats = (email, range, bucket) =>
  api.get(`/ai/leads/stats`, { params: { email, range, bucket } }).then(r => r.data);
export const fetchAiSuggestions = (email, lang = "en") =>
  api.get(`/ai/suggestions`, { params: { email, lang } }).then(r => r.data);
export const fetchAiPromoteRecipients = (payload) =>
  api.post(`/ai/promote/recipients`, payload).then(r => r.data);
export const fetchAiPromoteDraft = (payload) =>
  api.post(`/ai/promote/draft`, payload).then(r => r.data);
export const listAiPromoteDrafts = (email, limit = 50) =>
  api.get(`/ai/promote/drafts`, { params: { email, limit } }).then(r => r.data);
export const fetchAiManual = (role, email = "") =>
  api.get(`/ai/manual/${role}`, { params: { email } }).then(r => r.data);
export const aiManualDownloadUrl = (role, email, format = "pdf") => {
  const base = api.defaults.baseURL || "";
  const params = new URLSearchParams({ email: email || "", format });
  return `${base}/ai/manual/${role}/download?${params.toString()}`;
};
export const fetchWhatsNew = (email) =>
  api.get(`/ai/whats-new`, { params: { email } }).then(r => r.data);
export const markWhatsNewSeen = (requester_email) =>
  api.post(`/ai/whats-new/seen`, { requester_email }).then(r => r.data);

// Part specs (admin-only)
export const fetchPartSpecs = (requester_email, for_class = "") => api.get("/part-specs", { params: { requester_email, for_class } }).then(r => r.data);
export const addPartSpec = (payload) => api.post("/part-specs", payload).then(r => r.data);

// People directory — resolves emails to display names
export const fetchPeople = () => api.get("/people").then(r => r.data);

// Part files
export const fetchPartFiles = (partId, audience = "all") => api.get(`/parts/${partId}/files`, { params: { audience } }).then(r => r.data);
export const resolveDriveFile = (name, kind = "file") => api.get("/drive/resolve", { params: { name, kind } }).then(r => r.data);

// My-uploads inbox
export const fetchMyUploads = () => api.get("/my-uploads").then(r => r.data);
export const fetchMyUploadsReps = () => api.get("/my-uploads/reps").then(r => r.data);
export const shareMyUpload = (fileId, payload = {}) =>
  api.post(`/my-uploads/${encodeURIComponent(fileId)}/share`, payload).then(r => r.data);

// Public Files — MySQL `Email Data` catalogue (ACC/USAir documents)
export const fetchPublicFiles = (params = {}) =>
  api.get("/public-files", { params }).then(r => r.data);
export const fetchPublicFileClasses = () =>
  api.get("/public-files/classes").then(r => r.data);
export const resolvePublicFiles = (query, limit = 10) =>
  api.post("/public-files/resolve", { query, limit }).then(r => r.data);
export const sendPublicFiles = (payload) =>
  api.post("/public-files/send", payload).then(r => r.data);
export const adminUpdatePublicFile = (id, patch) =>
  api.put(`/public-files/${id}`, patch).then(r => r.data);
export const adminDeletePublicFile = (id) =>
  api.delete(`/public-files/${id}`).then(r => r.data);
export const adminCreatePublicFile = (file, meta, onProgress) => {
  const fd = new FormData();
  fd.append("upload", file);
  fd.append("file_type", meta.file_type);
  if (meta.file_class) fd.append("file_class", meta.file_class);
  if (meta.explanation) fd.append("explanation", meta.explanation);
  fd.append("available_for_customer", meta.available_for_customer ? "true" : "false");
  fd.append("allowed_with_quote", meta.allowed_with_quote ? "true" : "false");
  fd.append("display_name", meta.display_name || file.name);
  return api.post("/public-files", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress
      ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total || 1)))
      : undefined,
  }).then(r => r.data);
};
export const publicFileDownloadUrl = (id) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/public-files/${encodeURIComponent(id)}/download`;
export const publicFilePictureUrl = (id) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/public-files/${encodeURIComponent(id)}/picture`;

// Public-file send templates (saved selections)
export const fetchPublicFileTemplates = () =>
  api.get("/public-file-templates").then(r => r.data);
export const createPublicFileTemplate = (payload) =>
  api.post("/public-file-templates", payload).then(r => r.data);
export const updatePublicFileTemplate = (id, payload) =>
  api.put(`/public-file-templates/${id}`, payload).then(r => r.data);
export const deletePublicFileTemplate = (id) =>
  api.delete(`/public-file-templates/${id}`).then(r => r.data);

// Upload a file (or photo / video on mobile) to a Part. `opts` may include
// audience, explanation, file_type, and `onProgress(pct)` for the UI.
export const uploadPartFile = (partId, file, opts = {}) => {
  const fd = new FormData();
  fd.append("upload", file);
  if (opts.audience) fd.append("audience", opts.audience);
  if (opts.explanation) fd.append("explanation", opts.explanation);
  if (opts.file_type) fd.append("file_type", opts.file_type);
  return api.post(`/parts/${partId}/files`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (opts.onProgress && evt.total) {
        opts.onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    },
  }).then((r) => r.data);
};

// Admin: files base URL
export const getFilesBaseUrl = (requester_email) => api.get("/admin/files-base-url", { params: { requester_email } }).then(r => r.data);
export const setFilesBaseUrl = (url, requester_email) => api.put("/admin/files-base-url", { url, requester_email }).then(r => r.data);

// Admin: Google Drive config
export const getDriveConfig = (requester_email) => api.get("/admin/drive-config", { params: { requester_email } }).then(r => r.data);
export const testDriveConfig = (payload) => api.post("/admin/drive-config/test", payload).then(r => r.data);
export const saveDriveConfig = (payload) => api.put("/admin/drive-config", payload).then(r => r.data);

// Super-admin: Mongo migration import
export const fetchMongoMigrationSchema = () =>
  api.get("/admin/mongo-migration/schema").then(r => r.data);

export const fetchMongoMigrationImportProgress = (importId) =>
  api.get(`/admin/mongo-migration/import/progress/${encodeURIComponent(importId)}`, { silent: true }).then(r => r.data);

export const previewMongoMigrationImport = (file, opts = {}) => {
  const fd = new FormData();
  fd.append("package", file);
  return api.post("/admin/mongo-migration/import/preview", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (opts.onProgress && evt.total) {
        opts.onProgress({
          loaded: evt.loaded,
          total: evt.total,
          percent: Math.round((evt.loaded / evt.total) * 100),
        });
      }
    },
  }).then(r => r.data);
};

export const runMongoMigrationImport = (file, { mode = "replace", confirm = false, importId = "", onProgress } = {}) => {
  const fd = new FormData();
  fd.append("package", file);
  fd.append("mode", mode);
  fd.append("confirm", confirm ? "true" : "false");
  if (importId) fd.append("import_id", importId);
  return api.post("/admin/mongo-migration/import", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) {
        onProgress({
          loaded: evt.loaded,
          total: evt.total,
          percent: Math.round((evt.loaded / evt.total) * 100),
        });
      }
    },
  }).then(r => r.data);
};

// Export
export const exportPriceList = (data) => api.post("/export", data, { responseType: "blob" }).then(r => r);

// Cart checkout
export const cartCheckout = (payload) => api.post("/cart/checkout", payload).then(r => r.data);
export const previewCustomerTier = (customerId) => api.get(`/cart/preview-tier/${encodeURIComponent(customerId)}`).then(r => r.data);

// Generate a public share link for a quote (admin-only)
export const createQuoteShareLink = (quoteId) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/share-link`).then(r => r.data);

// Quote change history (for the admin Quote page)
export const fetchQuoteHistory = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/history`, { params }).then(r => r.data);

// Related quotes for the same customer (admin/owner/magic-token)
export const fetchQuoteRelated = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/related`, { params }).then(r => r.data);

// Payment summary (Journal Entry rows linked to this quote — for header chip)
export const fetchQuotePayments = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/payments`, { params }).then(r => r.data);

// v1 snapshot + current state + computed diffs
export const fetchQuoteVersions = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/versions`, { params }).then(r => r.data);

// Absolute URL to the Proforma Invoice PDF for this quote
export const quotePdfUrl = (quoteId, magicToken = "") => {
  const base = `${BACKEND_URL}/api/quotes/${encodeURIComponent(quoteId)}/pdf`;
  return magicToken ? `${base}?t=${encodeURIComponent(magicToken)}` : base;
};

// Quote comments / replies thread
export const fetchQuoteComments = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/comments`, { params }).then(r => r.data);

export const postQuoteComment = (quoteId, payload) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/comments`, payload).then(r => r.data);

// Upload an attachment to a quote (pre-comment). Returns the attachment metadata.
export const uploadQuoteAttachment = (quoteId, file, magicToken = "") => {
  const form = new FormData();
  form.append("file", file);
  if (magicToken) form.append("magic_token", magicToken);
  return api.post(`/quotes/${encodeURIComponent(quoteId)}/attachments`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then(r => r.data);
};

// Promote a Preliminary draft to a final Quote (admin-only)
export const promoteQuote = (quoteId) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/promote`).then(r => r.data);

export const sendQuoteToCustomer = (quoteId, payload) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/send`, payload).then(r => r.data);

export const fetchQuoteEmailSends = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/email-sends`, { params }).then(r => r.data);

// Compressor lines on a quote that have a Preventive Maintenance schedule
export const fetchQuotePmCandidates = (quoteId) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/pm-candidates`).then(r => r.data);

// Typing indicator (heartbeat + poll) for a quote's comments thread
export const sendTypingHeartbeat = (quoteId, payload) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/comments/typing`, payload).then(r => r.data);

export const fetchTypingUsers = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/comments/typing`, { params }).then(r => r.data);

export default api;

// ─── Access Control (RBAC) ────────────────────────────────────────
export const fetchAccessMe = () => api.get("/access/me").then(r => r.data);
export const fetchAccessPermissions = () => api.get("/access/permissions").then(r => r.data);
export const fetchAccessRoles = () => api.get("/access/roles").then(r => r.data);
export const fetchAccessUsers = (params = {}) => api.get("/access/users", { params }).then(r => r.data);
export const upsertAccessUser = (payload) => api.put("/access/users", payload).then(r => r.data);
export const deleteAccessUser = (email) => api.delete(`/access/users/${encodeURIComponent(email)}`).then(r => r.data);
export const searchAccessPeople = (params) => api.get("/access/people/search", { params }).then(r => r.data);
export const upsertAccessRole = (payload) => api.post("/access/roles", payload).then(r => r.data);
export const deleteAccessRole = (role) => api.delete(`/access/roles/${encodeURIComponent(role)}`).then(r => r.data);

// Access-control advisor (deterministic overlap + LLM agent)
export const advisorCheckPermission = (payload) => api.post("/access/agent/check-permission", payload).then(r => r.data);
export const advisorCheckRole = (payload) => api.post("/access/agent/check-role", payload).then(r => r.data);
export const advisorAnalyseAssignment = (payload) => api.post("/access/agent/analyse-assignment", payload).then(r => r.data);
export const advisorChat = (prompt) => api.post("/access/agent/chat", { prompt }).then(r => r.data);
export const advisorApply = (payload) => api.post("/access/agent/apply", payload).then(r => r.data);
export const advisorUndo = (applyId) => api.post(`/access/agent/undo/${encodeURIComponent(applyId)}`).then(r => r.data);
export const advisorHistory = (params = {}) => api.get("/access/agent/history", { params }).then(r => r.data);
export const fetchRecentQuoteChanges = (params = {}) => api.get("/quotes-feed/recent", { params }).then(r => r.data);

// Email routing (admin)
export const fetchEmailRouting = (requester_email) => api.get("/admin/email/routing", { params: { requester_email } }).then(r => r.data);
export const saveEmailRouting = (payload) => api.put("/admin/email/routing", payload).then(r => r.data);
export const sendTestEmail = (payload) => api.post("/admin/email/test", payload).then(r => r.data);


// ─── PM Schedule ───
export const fetchPmSchedule = (product_type, shifts = 1, years = 10, currency = "USD", include_prices = true) =>
  api.get("/compressors/pm-schedule", {
    params: { product_type, shifts, years, currency, include_prices },
  }).then(r => r.data);

export const fetchStarterKits = (product_type, currency = "USD", shifts = 1, years = 3) =>
  api.get("/compressors/starter-kits", {
    params: { product_type, currency, shifts, years },
  }).then(r => r.data);

export const pmSchedulePdfUrl = ({ product_type, shifts = 1, years = 10, brand = "US", customer_name = "", quote_id = "", include_prices = true }) => {
  const qs = new URLSearchParams({
    product_type, shifts: String(shifts), years: String(years), brand,
    include_prices: String(!!include_prices),
    ...(customer_name ? { customer_name } : {}),
    ...(quote_id ? { quote_id } : {}),
  }).toString();
  return `${BACKEND_URL}/api/compressors/pm-schedule/pdf?${qs}`;
};

// ─── Twilio: Video / SMS / Whisper transcription ───────────────────
export const twilioHealth = () => api.get("/twilio/health").then(r => r.data);
export const mintVideoToken = ({ room, identity, customer_id }) =>
  api.post("/twilio/video/token", { room, identity, customer_id }).then(r => r.data);
export const logVideoEnd = ({ room, note, duration_seconds }) =>
  api.post("/twilio/video/log", { room, note, duration_seconds }).then(r => r.data);
export const fetchCustomerCalls = (customer_id, limit = 50) =>
  api.get(`/twilio/calls/${encodeURIComponent(customer_id)}`, { params: { limit } }).then(r => r.data);
export const sendSms = ({ customer_id, person_id, to, body }) =>
  api.post("/twilio/sms/send", { customer_id, person_id, to, body }).then(r => r.data);
export const fetchSmsThread = (customer_id, limit = 200, phone = null) =>
  api.get(`/twilio/sms/threads/${encodeURIComponent(customer_id)}`,
          { params: { limit, ...(phone ? { phone } : {}) } }).then(r => r.data);
export const fetchSmsThreadByPhone = (phone, limit = 200) => {
  // urlsafe-base64 of the phone (server tolerates missing padding)
  const b64 = btoa(unescape(encodeURIComponent(String(phone || ""))))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return api.get(`/twilio/sms/threads-by-phone/${b64}`, { params: { limit } }).then(r => r.data);
};
export const searchPersonsForLink = (q, limit = 20) =>
  api.get("/twilio/sms/persons/search", { params: { q, limit } }).then(r => r.data);
export const linkOrphanToPerson = (payload) =>
  api.post("/twilio/sms/orphan/link", payload).then(r => r.data);

export const markConversationUnread = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.post(`/twilio/sms/orphan-thread/mark-unread`, null,
                    { params: { phone_number } }).then(r => r.data);
  }
  return api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/mark-unread`, null,
                  { params: { ...(person_id ? { person_id } : {}),
                              ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const blockNumber = (phone, reason) =>
  api.post(`/twilio/sms/blocked-numbers`, { phone, reason }).then(r => r.data);
export const unblockNumber = (phone) =>
  api.delete(`/twilio/sms/blocked-numbers`, { params: { phone } }).then(r => r.data);
export const fetchBlockedNumbers = (limit = 200) =>
  api.get(`/twilio/sms/blocked-numbers`, { params: { limit } }).then(r => r.data);
export const fetchAllConversations = (limit = 100, archived = false) =>
  api.get(`/twilio/sms/conversations`, { params: { limit, archived } }).then(r => r.data);
export const archiveConversation = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.post(`/twilio/sms/orphan-thread/archive`, null,
                    { params: { phone_number } }).then(r => r.data);
  }
  return api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/archive`, null,
                  { params: { ...(person_id ? { person_id } : {}),
                              ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const snoozeConversation = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.post(`/twilio/sms/orphan-thread/archive`, null,
                    { params: { phone_number, snooze: true } }).then(r => r.data);
  }
  return api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/snooze`, null,
                  { params: { ...(person_id ? { person_id } : {}),
                              ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const unarchiveConversation = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.post(`/twilio/sms/orphan-thread/unarchive`, null,
                    { params: { phone_number } }).then(r => r.data);
  }
  return api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/unarchive`, null,
                  { params: { ...(person_id ? { person_id } : {}),
                              ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const autoArchiveStaleThreads = (stale_days = 90) =>
  api.post(`/twilio/sms/conversations/auto-archive-stale`, null,
           { params: { stale_days } }).then(r => r.data);
export const markConversationSeen = (customer_id, person_id) =>
  api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/mark-seen`, null,
           { params: person_id ? { person_id } : {} }).then(r => r.data);
export const deleteConversation = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.delete(`/twilio/sms/orphan-thread`,
                      { params: { phone_number } }).then(r => r.data);
  }
  return api.delete(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}`,
                    { params: { ...(person_id ? { person_id } : {}),
                                ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const refreshSmsStatus = (customer_id) =>
  api.post(`/twilio/sms/refresh-status/${encodeURIComponent(customer_id)}`).then(r => r.data);
export const transcribeAudio = ({ blob, customer_id, call_id, save_as_note = true }) => {
  const fd = new FormData();
  fd.append("audio", blob, blob.name || "call.webm");
  fd.append("customer_id", customer_id);
  if (call_id) fd.append("call_id", call_id);
  fd.append("save_as_note", String(save_as_note));
  return api.post("/twilio/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};

// ─── Service-Tech on-site capture ──────────────────────────────────
export const fetchServiceTechFleet = (q = "", limit = 200) =>
  api.get("/service-tech/fleet", { params: { q, limit } }).then(r => r.data);
export const fetchServiceTechOpenQuote = (serial_id) =>
  api.get(`/service-tech/fleet/${encodeURIComponent(serial_id)}/open-quote`).then(r => r.data);
export const createServiceVisit = (payload) =>
  api.post("/service-tech/visits", payload).then(r => r.data);
export const listServiceVisits = (limit = 50) =>
  api.get("/service-tech/visits", { params: { limit } }).then(r => r.data);
export const getServiceVisit = (visit_id) =>
  api.get(`/service-tech/visits/${encodeURIComponent(visit_id)}`).then(r => r.data);
export const finalizeServiceVisit = (visit_id, payload = {}) =>
  api.post(`/service-tech/visits/${encodeURIComponent(visit_id)}/finalize`, payload).then(r => r.data);
export const fetchServiceTechDashboard = (params = {}) =>
  api.get("/service-tech/dashboard", { params }).then(r => r.data);
export const previewServiceTechDigest = (params = {}) =>
  api.get("/service-tech/digest/preview", { params }).then(r => r.data);
export const sendServiceTechDigest = (payload = {}) =>
  api.post("/service-tech/digest/send", payload).then(r => r.data);
export const previewServiceVisitSn = (visit_id) =>
  api.post(`/service-tech/visits/${encodeURIComponent(visit_id)}/preview-sn`).then(r => r.data);
export const fetchServiceTechLeaderboard = (days = 30) =>
  api.get("/service-tech/leaderboard", { params: { days } }).then(r => r.data);

// ─── Employee Positions admin ──────────────────────────────────────
export const fetchPositions = (q = "") => api.get("/positions", { params: { q } }).then(r => r.data);
export const createPosition = (payload) => api.post("/positions", payload).then(r => r.data);
export const updatePosition = (ep_id, payload) =>
  api.put(`/positions/${encodeURIComponent(ep_id)}`, payload).then(r => r.data);
export const deletePosition = (ep_id) => api.delete(`/positions/${encodeURIComponent(ep_id)}`).then(r => r.data);
export const uploadServiceVisitMedia = (visit_id, kind, file, opts = {}) => {
  const fd = new FormData();
  fd.append("upload", file, file.name || `${kind}.bin`);
  fd.append("kind", kind);
  return api.post(`/service-tech/visits/${encodeURIComponent(visit_id)}/media`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (opts.onProgress && evt.total) {
        opts.onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    },
  }).then(r => r.data);
};
export const serviceVisitMediaUrl = (media_id) =>
  `${BACKEND_URL}/api/service-tech/media/${encodeURIComponent(media_id)}`;


// Call routing + staff-position grid (iter54)
export const fetchCallRoutingRules = () => api.get("/admin/call-routing/rules").then(r => r.data);
export const updateCallRoutingRules = (data) => api.put("/admin/call-routing/rules", data).then(r => r.data);
export const fetchStaffPositions = () => api.get("/admin/staff-positions").then(r => r.data);
export const updateStaffPosition = (data) => api.patch("/admin/staff-positions", data).then(r => r.data);
export const previewVoiceWebhooks = () => api.get("/admin/twilio-credentials/voice-config/preview").then(r => r.data);
export const syncVoiceWebhooks = (phone_sids = null) => api.post("/admin/twilio-credentials/voice-config/sync", { phone_sids }).then(r => r.data);
export const fetchWarrantyBriefings = (params = {}) => api.get("/admin/warranty-briefings", { params }).then(r => r.data);
export const fetchMyRecentBriefings = (limit = 20) => api.get("/me/recent-briefings", { params: { limit } }).then(r => r.data);
export const fetchErrorLog = (requester_email, params = {}) => api.get("/admin/error-log", { params: { requester_email, ...params } }).then(r => r.data);
export const fetchErrorRow = (incident_id, requester_email) => api.get(`/admin/error-log/${encodeURIComponent(incident_id)}`, { params: { requester_email } }).then(r => r.data);

// iter136.28 — PWA branding (super-admin-editable title + icons)
export const fetchPwaBranding = () => api.get("/pwa/branding").then(r => r.data);
export const updatePwaBranding = (patch) => api.put("/pwa/branding", patch).then(r => r.data);
export const resetPwaBranding = () => api.delete("/pwa/branding").then(r => r.data);
export const uploadPwaIcon = (file) => {
  const fd = new FormData();
  fd.append("upload", file);
  return api.post("/pwa/icon", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};


// ─── HR Admin (iter147) ──────────────────────────────────────────
export const fetchHrStaff = () => api.get("/hr/staff").then(r => r.data);
export const fetchHrInsights = () => api.get("/hr/insights").then(r => r.data);
export const fetchHrLibrary = (params = {}) => api.get("/hr/library", { params }).then(r => r.data);
export const createHrLibraryItem = (payload) => api.post("/hr/library", payload).then(r => r.data);
export const updateHrLibraryItem = (id, payload) => api.put(`/hr/library/${id}`, payload).then(r => r.data);
export const deleteHrLibraryItem = (id) => api.delete(`/hr/library/${id}`).then(r => r.data);
export const publishHrLibraryItem = (id) => api.post(`/hr/library/${id}/publish`).then(r => r.data);
export const generateHrMaterial = (payload) => api.post("/hr/ai/generate", payload).then(r => r.data);
export const sendHrChatTurn = (payload) => api.post("/hr/ai/chat", payload).then(r => r.data);
export const fetchHrChatSession = (sessionId) => api.get(`/hr/ai/chat/${sessionId}`).then(r => r.data);
export const translateHrLibraryItem = (id) => api.post(`/hr/library/${id}/translate`).then(r => r.data);
export const recordHrLibraryView = (id) => api.post(`/hr/library/${id}/view`).then(r => r.data);

// Checklists (iter148)
export const fetchHrTemplates = () => api.get("/hr/checklists/templates").then(r => r.data);
export const createHrTemplate = (p) => api.post("/hr/checklists/templates", p).then(r => r.data);
export const updateHrTemplate = (id, p) => api.put(`/hr/checklists/templates/${id}`, p).then(r => r.data);
export const deleteHrTemplate = (id) => api.delete(`/hr/checklists/templates/${id}`).then(r => r.data);
export const fetchHrAssignments = () => api.get("/hr/checklists/assignments").then(r => r.data);
export const createHrAssignments = (p) => api.post("/hr/checklists/assignments", p).then(r => r.data);
export const deleteHrAssignment = (id) => api.delete(`/hr/checklists/assignments/${id}`).then(r => r.data);
export const fetchMyChecklist = () => api.get("/hr/checklists/me").then(r => r.data);
export const ackMyChecklistItem = (item_id) => api.post("/hr/checklists/me/ack", { item_id }).then(r => r.data);
export const hrMarkChecklistItem = (p) => api.post("/hr/checklists/hr-mark", p).then(r => r.data);

// Admin manuals JSON editor (iter149c)
export const fetchManualFiles = () => api.get("/admin/manuals").then(r => r.data);
export const fetchManualFile = (name) => api.get(`/admin/manuals/${name}`).then(r => r.data);
export const saveManualFile = (name, content) => api.put(`/admin/manuals/${name}`, { content }).then(r => r.data);



